// components/WaterLevelControllerPage.jsx

import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Grid,
  TextField,
  Divider,
  Switch,
  Snackbar,
  Alert,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/config';
import ResetDialog from './ResetDialog';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import DeleteControllerDialog from './DeleteControllerDialog';
import AlertSettingsDialog from './AlertSettingsDialog';
import SchedulerDialog from './SchedulerDialog';
import ControllerEditDialog from './ControllerEditDialog';
import { saveAs } from 'file-saver';

/* ---------------------------------------------------------------------------
   STYLED COMPONENTS
   -------------------------------------------------------------------------*/


dayjs.extend(duration);
dayjs.extend(relativeTime);

/* ---------------------------------------------------------------------------
   MAIN COMPONENT
   -------------------------------------------------------------------------*/

const WaterLevelControllerPage = ({ controllerData, device, sensors, userPermission }) => {
  console.log(controllerData)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const navigate = useNavigate();

  /* ------------------------- STATE -------------------------*/
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1);
  const [monthlyLogs, setMonthlyLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [motorStatus, setMotorStatus] = useState(controllerData?.controllerData?.motorStatus || 'OFF');
  const [loadingMotorToggle, setLoadingMotorToggle] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showScheduler, setShowScheduler] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [showAlertSettingsDialog, setShowAlertSettingsDialog] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isControllerEditDialogOpen, setIsControllerEditDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [yearlyLogs, setYearlyLogs] = useState([]);

  /* ------------------------- CONSTANTS -------------------------*/
  const today = dayjs().format('DD-MM-YYYY');
  const motorOnTimestamp = controllerData?.controllerData?.motorOnTimestamp;
  const motorOffTimestamp = controllerData?.controllerData?.motorOffTimestamp;
  const offOnSince = controllerData?.controllerData?.offOnSince || {
    hours: 0,
    minutes: 0,
    totalMinutes: 0,
  };

  /* -----------------------------------------------------------------------
     EFFECTS –  FETCH LOGS FROM NEW APIs
     ---------------------------------------------------------------------*/

  // Get auth token from localStorage
  const getAuthToken = () => {
    const token = localStorage.getItem('token');
    return token;
  };

  // Fetch today's logs
  useEffect(() => {
    const fetchTodayLogs = async () => {
      try {
        const token = getAuthToken();
        const today = dayjs().format('YYYY-MM-DD');
        // The API keys off the device code — it resolves the controller itself.
        const deviceCode = controllerData?.device_code;
        const response = await axios.get(
          `${API_BASE_URL}api/motor-logs/${deviceCode}/today?date=${today}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.logs) {
          setFilteredLogs(response.data.logs);
        }
      } catch (error) {
        console.error('Error fetching today logs:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to fetch today\'s logs', 
          severity: 'error' 
        });
      }
    };

    if (controllerData?.device_code) {
      fetchTodayLogs();
    }
  }, [controllerData?.device_code]);

  // Fetch daily logs (monthly view)
  useEffect(() => {
    const fetchDailyLogs = async () => {
      try {
        const token = getAuthToken();
        const deviceCode = controllerData?.device_code;
        const response = await axios.get(
          `${API_BASE_URL}api/motor-logs/${deviceCode}/daily?year=${dayjs().year()}&month=${selectedMonth}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.dailyLogs) {
          const formattedLogs = response.data.dailyLogs.map(log => ({
            day: log.dateFormatted,
            duration: log.totalRunDurationFormatted
          }));
          setMonthlyLogs(formattedLogs);
        }
      } catch (error) {
        console.error('Error fetching daily logs:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to fetch daily logs', 
          severity: 'error' 
        });
      }
    };

    if (controllerData?.device_code) {
      fetchDailyLogs();
    }
  }, [selectedMonth, controllerData?.device_code]);

  // Fetch monthly logs (yearly view)
  useEffect(() => {
    const fetchMonthlyLogs = async () => {
      try {
        const token = getAuthToken();
        const deviceCode = controllerData?.device_code;
        const response = await axios.get(
          `${API_BASE_URL}api/motor-logs/${deviceCode}/monthly?year=${selectedYear}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data && response.data.monthlyLogs) {
          const yearData = response.data.monthlyLogs.map(log => ({
            month: log.monthName,
            duration: log.totalRunDurationFormatted,
            totalRunDuration: log.totalRunDuration
          }));
          setYearlyLogs(yearData);
        }
      } catch (error) {
        console.error('Error fetching monthly logs:', error);
        setSnackbar({ 
          open: true, 
          message: 'Failed to fetch monthly logs', 
          severity: 'error' 
        });
      }
    };

    // Only fetch if we have controller data
    if (controllerData?.device_code) {
      fetchMonthlyLogs();
    }
  }, [selectedYear, controllerData?.device_code]);

  /* -----------------------------------------------------------------------
     HELPERS
     ---------------------------------------------------------------------*/
  const formatDuration = (totalMinutes) => {
    if (totalMinutes === null || totalMinutes === undefined) return 'N/A';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const formatMotorStatus = () => {
    if (motorStatus === 'PLEASE_WAIT') return <span>{motorStatus}</span>;
    if (!offOnSince || offOnSince.totalMinutes <= 0) return <span>{`${motorStatus} (For: Just Now)`}</span>;

    const now = dayjs();
    const dateSince = now.subtract(offOnSince.totalMinutes, 'minute');
    const formattedAtTime = dateSince.format('hh:mm A, ddd DD MMM YYYY');
    const forString = `${offOnSince.hours}h ${offOnSince.minutes}m`;
    const lastUpdateTimestamp = motorStatus === 'ON' ? motorOnTimestamp : motorOffTimestamp;
    const formattedLastUpdate = lastUpdateTimestamp ? dayjs(lastUpdateTimestamp).format('hh:mm A, ddd DD MMM YYYY') : 'Just Now';

    return (
      <div>
        <div>{`${motorStatus} at ${formattedAtTime} (For: ${forString})`}</div>
        <div style={{ fontSize: '0.875rem', color: 'gray' }}>Last update: {formattedLastUpdate}</div>
      </div>
    );
  };

  let motorStatusColor = motorStatus === 'ON' ? 'red' : motorStatus === 'PLEASE_WAIT' ? 'orange' : 'green';

  /* -----------------------------------------------------------------------
     ACTIONS –  MOTOR TOGGLE / DOWNLOAD / MENU
     ---------------------------------------------------------------------*/
  const handleMotorToggle = async () => {
    if (userPermission !== 'edit') {
      setSnackbar({ open: true, message: 'You do not have permission to toggle the motor.', severity: 'error' });
      return;
    }

    setLoadingMotorToggle(true);
    const desiredState = motorStatus === 'ON' ? 'OFF' : 'ON';
    try {
      const response = await axios.post(`${API_BASE_URL}api/sensor/toggle-motor/${controllerData.device_code}`, { state: desiredState });
      const updatedControllerData = response.data;
      setMotorStatus(updatedControllerData.motorStatus || 'OFF');
      setSnackbar({ open: true, message: updatedControllerData.message, severity: 'info' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to toggle motor state. Please try again.', severity: 'error' });
    } finally {
      setLoadingMotorToggle(false);
    }
  };

  const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleHistoryClick = () => {
    navigate('/history/controller-history', { state: { controllerData } });
    handleMenuClose();
  };

  const handleDeleteSuccess = () => {
    navigate(`/control-panel/${device.deviceId}/${device.deviceName}`);
    window.location.reload();
  };

  /* --------------------------- DOWNLOAD HELPERS ---------------------------*/

  const createCanvas = (title, logs, columns) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = 1400;
      canvas.height = 900;

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = 'bold 28px Arial';
      ctx.fillStyle = '#1976d2';
      ctx.textAlign = 'center';
      ctx.fillText(title, canvas.width / 2, 50);

      ctx.font = '18px Arial';
      ctx.fillStyle = '#424242';
      ctx.textAlign = 'left';

      const deviceInfo = [
        `Device: ${device?.deviceName || 'Unknown Device'}`,
        `ID: ${device?.deviceId || 'N/A'}`,
        `Location: ${device?.location || 'N/A'}`,
        `Downloaded: ${dayjs().format('DD-MM-YYYY HH:mm:ss')}`,
      ];
      deviceInfo.forEach((info, index) => ctx.fillText(info, 60, 90 + index * 28));

      const tableStartY = 200;
      const rowHeight = 30;
      const columnWidths = [100, 300, 300, 300];
      let currentX = 60;

      ctx.font = 'bold 16px Arial';
      ctx.fillStyle = '#1976d2';
      ctx.textAlign = 'left';
      columns.forEach((col, idx) => {
        ctx.fillText(col, currentX, tableStartY);
        currentX += columnWidths[idx];
      });

      ctx.font = '14px Arial';
      ctx.fillStyle = '#333';
      logs.forEach((log, rowIdx) => {
        const rowY = tableStartY + rowHeight * (rowIdx + 1);
        currentX = 60;
        columns.forEach((col, colIdx) => {
          let value;
          switch (col) {
            case 'Serial No.':
              value = log.serialNumber;
              break;
            case 'Start Time':
              value = log.startTime;
              break;
            case 'Stop Time':
              value = log.stopTime === 'Running' ? 'Running' : log.stopTime;
              break;
            case 'Recent Run Duration':
              value = log.runDurationFormatted;
              break;
            case 'Date':
              value = log.day || 'N/A';
              break;
            case 'Run Duration (Daily)':
              value = log.duration || 'N/A';
              break;
            case 'Month':
              value = log.month || 'N/A';
              break;
            case 'Run Duration (Monthly)':
              value = log.duration || 'N/A';
              break;
            default:
              value = 'N/A';
          }
          ctx.fillText(value, currentX, rowY);
          currentX += columnWidths[colIdx];
        });
      });

      ctx.font = 'bold 14px Arial';
      ctx.fillStyle = '#1976d2';
      ctx.textAlign = 'center';
      ctx.fillText('Generated from Alphatech Water Level Monitoring System', canvas.width / 2, 810);
      ctx.font = '12px Arial';
      ctx.fillStyle = '#666';
      ctx.fillText('For technical assistance or queries, please contact our support team', canvas.width / 2, 865);
      resolve(canvas);
    });
  };

  const downloadLogsAsJPG = async (title, logs, columns) => {
    if (downloading) return;
    setDownloading(true);
    try {
      const canvas = await createCanvas(title, logs, columns);
      canvas.toBlob((blob) => {
        const fileName = `${title.replace(/\s+/g, '_')}_${today.replace(/[\/\\?%*:|"<>]/g, '-')}.jpg`;
        saveAs(blob, fileName);
        setDownloading(false);
      }, 'image/jpeg', 0.95);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Failed to download logs. Please try again.', severity: 'error' });
      setDownloading(false);
    }
  };

  const downloadTodaysLog = () => {
    const title = `Today's Log (${selectedDate.format('DD-MM-YYYY')})`;
    const columns = ['Serial No.', 'Start Time', 'Stop Time', 'Recent Run Duration'];
    downloadLogsAsJPG(title, filteredLogs, columns);
  };

  const downloadMonthlyLog = () => {
    const monthName = dayjs(new Date(0, selectedMonth - 1)).format('MMMM');
    const title = `Monthly Log (${monthName} ${dayjs().year()})`;
    const columns = ['Date', 'Run Duration (Daily)'];
    downloadLogsAsJPG(title, monthlyLogs, columns);
  };

// Update the download yearly log function

const downloadYearlyLog = () => {
  const title = `Monthly Log (${selectedYear})`;
  const columns = ['Month', 'Run Duration (Monthly)'];
  
  // Create a formatted version of yearly logs for download
  const formattedLogs = yearlyLogs.map(log => ({
    month: log.month,
    duration: log.duration
  }));
  
  downloadLogsAsJPG(title, formattedLogs, columns);
};

  /* -----------------------------------------------------------------------
     RENDER
     ---------------------------------------------------------------------*/
  return (
    <Box display="flex" flexDirection="column" alignItems="center" sx={{ width: '100%' }}>
      {/* ---------------------- HEADER ---------------------- */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%', px: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ color: '#1e88e5', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1.5 }}>
          Water Level Controller
        </Typography>
        <Button variant="contained" color="primary" sx={{ borderRadius: 2, fontWeight: 600 }} onClick={() => setShowScheduler(true)}>
          Scheduler
        </Button>
      </Box>

      {/* ---------------------- LOGS ---------------------- */}
      <Grid container spacing={4} justifyContent="center" alignItems="flex-start" sx={{ width: '100%', px: 3, mb: 4 }}>
        {/* Today Log */}
        <Grid item xs={12} md={4}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Today's Log</Typography>
              <Button size="small" variant="outlined" onClick={downloadTodaysLog} disabled={downloading || filteredLogs.length === 0} sx={{ ml: 1 }}>
                Download
              </Button>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                renderInput={(params) => <TextField {...params} sx={{ mb: 2, width: '100%' }} />}
              />
            </LocalizationProvider>
            <TableContainer component={Paper} sx={{ border: '1px solid #bdbdbd', borderRadius: '6px', maxHeight: 400, overflowY: 'auto', boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f5f5f5' }}>
                    <TableCell>Serial No.</TableCell>
                    <TableCell>Start Time</TableCell>
                    <TableCell>Stop Time</TableCell>
                    <TableCell>Recent Run Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs.length ? (
                    filteredLogs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{log.serialNumber}</TableCell>
                        <TableCell>{log.startTime}</TableCell>
                        <TableCell>{log.stopTime === 'Running' ? 'Running' : log.stopTime}</TableCell>
                        <TableCell>{log.runDurationFormatted}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No logs available for this date</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        </Grid>

        {/* Monthly Log */}
        <Grid item xs={12} md={4}>
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Daily Log</Typography>
              <Button size="small" variant="outlined" onClick={downloadMonthlyLog} disabled={downloading || monthlyLogs.length === 0} sx={{ ml: 1 }}>
                Download
              </Button>
            </Box>
            <FormControl variant="outlined" sx={{ mb: 2, minWidth: 120, width: '100%' }}>
              <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
                {[...Array(12).keys()].map((m) => (
                  <MenuItem key={m + 1} value={m + 1}>{dayjs(new Date(0, m)).format('MMMM')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TableContainer component={Paper} sx={{ border: '1px solid #bdbdbd', borderRadius: '6px', maxHeight: 300, overflowY: 'auto', boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f5f5f5' }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Run Duration (Daily)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {monthlyLogs.length ? (
                    monthlyLogs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{log.day}</TableCell>
                        <TableCell>{log.duration}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">No monthly logs available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        </Grid>

        {/* Yearly Log - New Section */}
        <Grid item xs={12} md={4}>
          <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Monthly Log</Typography>
              <Button size="small" variant="outlined" onClick={downloadYearlyLog} disabled={downloading || yearlyLogs.length === 0} sx={{ ml: 1 }}>
                Download
              </Button>
            </Box>
            <FormControl variant="outlined" sx={{ mb: 2, minWidth: 120, width: '100%' }}>
              <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
                {/* Show last 5 years */}
                {[...Array(5).keys()].map((idx) => {
                  const year = dayjs().year() - idx;
                  return <MenuItem key={year} value={year}>{year}</MenuItem>;
                })}
              </Select>
            </FormControl>
            <TableContainer component={Paper} sx={{ border: '1px solid #bdbdbd', borderRadius: '6px', maxHeight: 300, overflowY: 'auto', boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f5f5f5' }}>
                    <TableCell>Month</TableCell>
                    <TableCell>Run Duration (Monthly)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {yearlyLogs.length ? (
                    yearlyLogs.map((log, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{log.month}</TableCell>
                        <TableCell>{log.duration}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={2} align="center">No monthly logs available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </motion.div>
        </Grid>
      </Grid>

      {/* ---------------------- RESET BOX AT BOTTOM ---------------------- */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        sx={{ p: 2, backgroundColor: '#fff', borderRadius: 2, border: '1px solid #ff9800', textAlign: 'center', mt: 2, mb: 2, maxWidth: 500, mx: 'auto' }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', color: '#ff9800' }}>Controller Reset</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>Reset since: {controllerData?.controllerData?.resetDuration ? `${controllerData.controllerData.resetDuration.hours}h ${controllerData.controllerData.resetDuration.minutes}m` : 'N/A'}</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>Reset at: {controllerData?.controllerData?.resetDuration ? dayjs().subtract(controllerData.controllerData.resetDuration.totalMinutes, 'minute').format('hh:mm A, ddd DD MMM YYYY') : 'N/A'}</Typography>
        {userPermission === 'edit' && (
          <Button variant="outlined" color="error" onClick={() => setIsResetDialogOpen(true)} sx={{ borderRadius: 2, fontWeight: 600 }}>
            Reset Controller
          </Button>
        )}
      </Box>

      {/* ---------------------- LOGS ---------------------- */}
      
      {/* ---------------------- ERROR LOGS ---------------------- */}
      {/* <Grid container spacing={4} justifyContent="center" alignItems="flex-start" sx={{ width: '100%', px: 3 }}>
        <Grid item xs={12}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.9 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 500 }}>Errors</Typography>
            <ErrorsTable errorLogs={errorLogs} />
          </motion.div>
        </Grid>
      </Grid> */}

      {/* ---------------------- FOOTER ---------------------- */}
      <Divider sx={{ width: '100%', mt: 3, mb: 3 }} />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Dialogs */}
      {userPermission === 'edit' && (
        <>
          <ResetDialog open={isResetDialogOpen} onClose={() => setIsResetDialogOpen(false)} deviceCode={controllerData?.device_code} subId={controllerData?.sensor_sub_id} onResetSuccess={() => setSnackbar({ open: true, message: 'Reset successful.', severity: 'success' })} userPermission={userPermission} />
          <DeleteControllerDialog open={isDeleteDialogOpen} handleClose={() => setIsDeleteDialogOpen(false)} controller={controllerData} onDeleteSuccess={handleDeleteSuccess} />
          <ControllerEditDialog 
            open={isControllerEditDialogOpen} 
            handleClose={() => setIsControllerEditDialogOpen(false)} 
            device={controllerData} 
            refreshData={() => window.location.reload()} 
          />
        </>
      )}
      <AlertSettingsDialog open={showAlertSettingsDialog} onClose={() => setShowAlertSettingsDialog(false)} alertSettings={controllerData?.controllerData?.alertSettings} controllerId={controllerData?.controllerData?._id} onUpdated={(updated) => console.log('Updated alert settings:', updated)} />
      <SchedulerDialog open={showScheduler} onClose={(saved) => { setShowScheduler(false); if (saved) window.location.reload(); }} deviceCode={controllerData.device_code} />
    </Box>
  );
};

export default WaterLevelControllerPage;
